import { Box, Container, Typography, Paper, Divider, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { COLORS } from '@elevair/shared';

// Helper component for sections
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2, color: COLORS.secondary }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default function PrivacyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Elevair</title>
        <meta name="description" content="Elevair Privacy Policy - Learn how we collect, use, and protect your personal information." />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: COLORS.background, py: 6 }}>
        <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Link component={RouterLink} to="/" sx={{ textDecoration: 'none' }}>
              <Typography
                variant="h4"
                sx={{
                  color: COLORS.primary,
                  fontWeight: 700,
                  fontFamily: 'Georgia, serif',
                }}
              >
                Elevair
              </Typography>
            </Link>
          </Box>

          <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1, color: COLORS.secondary }}>
              Privacy Policy
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Last updated: January 13, 2026
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <Typography paragraph>
              This Privacy Policy describes how Elevair Inc. ("we," "us," or "our") collects, uses,
              and shares information about you when you use our website and services ("Service").
            </Typography>

            {/* Section 1 */}
            <Section title="1. Information We Collect">
              <Typography paragraph>
                <strong>Information You Provide:</strong>
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Account information (name, email, password)</Typography></li>
                <li><Typography>Business information (business name, contact details)</Typography></li>
                <li><Typography>Client and contact data you enter into the Service</Typography></li>
                <li><Typography>Content you create (notes, tasks, invoices, projects)</Typography></li>
                <li><Typography>Communications with us (support requests, feedback)</Typography></li>
                <li><Typography>Payment information (processed securely by Stripe)</Typography></li>
              </Box>
              <Typography paragraph sx={{ mt: 2 }}>
                <strong>Information Collected Automatically:</strong>
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Device information (browser type, operating system)</Typography></li>
                <li><Typography>Usage data (pages visited, features used)</Typography></li>
                <li><Typography>IP address and approximate location</Typography></li>
                <li><Typography>Cookies and similar tracking technologies</Typography></li>
              </Box>
            </Section>

            {/* Section 2 */}
            <Section title="2. How We Use Your Information">
              <Typography paragraph>
                We use the information we collect to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Provide, maintain, and improve the Service</Typography></li>
                <li><Typography>Process transactions and send related information</Typography></li>
                <li><Typography>Send you technical notices and support messages</Typography></li>
                <li><Typography>Respond to your comments and questions</Typography></li>
                <li><Typography>Analyze usage patterns to improve user experience</Typography></li>
                <li><Typography>Detect, prevent, and address fraud and security issues</Typography></li>
                <li><Typography>Comply with legal obligations</Typography></li>
              </Box>
            </Section>

            {/* Section 3 */}
            <Section title="3. Information Sharing">
              <Typography paragraph>
                We do not sell your personal information. We may share your information with:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography><strong>Service Providers:</strong> Third parties that help us operate the Service (hosting, payment processing, email delivery)</Typography></li>
                <li><Typography><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</Typography></li>
                <li><Typography><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</Typography></li>
                <li><Typography><strong>With Your Consent:</strong> When you explicitly agree to share information</Typography></li>
              </Box>
            </Section>

            {/* Section 4 */}
            <Section title="4. Data Security">
              <Typography paragraph>
                We implement appropriate technical and organizational measures to protect your information, including:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Encryption of data in transit (HTTPS/TLS)</Typography></li>
                <li><Typography>Encryption of sensitive data at rest</Typography></li>
                <li><Typography>Regular security assessments</Typography></li>
                <li><Typography>Access controls and authentication</Typography></li>
                <li><Typography>Secure password hashing</Typography></li>
              </Box>
              <Typography paragraph sx={{ mt: 2 }}>
                However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
              </Typography>
            </Section>

            {/* Section 5 */}
            <Section title="5. Data Retention">
              <Typography paragraph>
                We retain your information for as long as your account is active or as needed to provide the Service.
                We may also retain information to comply with legal obligations, resolve disputes, and enforce agreements.
              </Typography>
              <Typography paragraph>
                When you delete your account, we will delete or anonymize your personal information within 30 days,
                except where we are required to retain it by law.
              </Typography>
            </Section>

            {/* Section 6 */}
            <Section title="6. Your Rights">
              <Typography paragraph>
                Depending on your location, you may have the right to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Access the personal information we hold about you</Typography></li>
                <li><Typography>Correct inaccurate or incomplete information</Typography></li>
                <li><Typography>Delete your personal information</Typography></li>
                <li><Typography>Export your data in a portable format</Typography></li>
                <li><Typography>Object to certain processing of your information</Typography></li>
                <li><Typography>Withdraw consent where processing is based on consent</Typography></li>
              </Box>
              <Typography paragraph sx={{ mt: 2 }}>
                To exercise these rights, please contact us at privacy@elevair.app.
              </Typography>
            </Section>

            {/* Section 7 */}
            <Section title="7. Cookies and Tracking">
              <Typography paragraph>
                We use cookies and similar technologies to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Keep you logged in to your account</Typography></li>
                <li><Typography>Remember your preferences</Typography></li>
                <li><Typography>Understand how you use the Service</Typography></li>
                <li><Typography>Improve the Service based on usage patterns</Typography></li>
              </Box>
              <Typography paragraph sx={{ mt: 2 }}>
                You can control cookies through your browser settings. Note that disabling cookies may affect
                the functionality of the Service.
              </Typography>
            </Section>

            {/* Section 8 */}
            <Section title="8. Third-Party Services">
              <Typography paragraph>
                Our Service integrates with third-party services including:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography><strong>Stripe:</strong> Payment processing</Typography></li>
                <li><Typography><strong>SendGrid:</strong> Email delivery</Typography></li>
                <li><Typography><strong>Google Cloud:</strong> Hosting and file storage</Typography></li>
                <li><Typography><strong>MongoDB Atlas:</strong> Database services</Typography></li>
                <li><Typography><strong>Anthropic:</strong> AI-powered features</Typography></li>
              </Box>
              <Typography paragraph sx={{ mt: 2 }}>
                These services have their own privacy policies. We encourage you to review them.
              </Typography>
            </Section>

            {/* Section 9 */}
            <Section title="9. Children's Privacy">
              <Typography paragraph>
                The Service is not intended for children under 18 years of age. We do not knowingly collect
                personal information from children. If you believe we have collected information from a child,
                please contact us immediately.
              </Typography>
            </Section>

            {/* Section 10 */}
            <Section title="10. International Data Transfers">
              <Typography paragraph>
                Your information may be transferred to and processed in countries other than your own.
                We ensure appropriate safeguards are in place to protect your information in accordance
                with applicable laws.
              </Typography>
            </Section>

            {/* Section 11 */}
            <Section title="11. Changes to This Policy">
              <Typography paragraph>
                We may update this Privacy Policy from time to time. We will notify you of any material changes
                by posting the new policy on this page and updating the "Last updated" date.
              </Typography>
              <Typography paragraph>
                We encourage you to review this Privacy Policy periodically.
              </Typography>
            </Section>

            {/* Section 12 */}
            <Section title="12. Contact Us">
              <Typography paragraph>
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </Typography>
              <Typography paragraph>
                <strong>Email:</strong> privacy@elevair.app
                <br />
                <strong>Website:</strong> https://elevair.app
              </Typography>
            </Section>

            <Divider sx={{ my: 4 }} />

            {/* Footer Links */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Link component={RouterLink} to="/terms" sx={{ color: COLORS.accent }}>
                Terms of Service
              </Link>
              <Link component={RouterLink} to="/" sx={{ color: COLORS.accent }}>
                Back to Home
              </Link>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
